import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder } from '@angular/forms';
import { tap } from 'rxjs';

import {
  DiscussionCommitmentRating,
  DiscussionFormalQuestion,
  DiscussionModel,
  DiscussionRoleRating,
  DiscussionSeatRating,
  ValueRating,
} from '../../models/discussion.models';
import {
  DiscussionCustomQuestionFormValue,
  DiscussionFormGroup,
  DiscussionFormMode,
  Ratings,
} from '../../models/form/discussion-form.model';
import { forbiddenDiscussionFormValidator } from '../../validators/discussion-form.validators';

interface FormConfig {
  mode: DiscussionFormMode;
  discussion: DiscussionModel;
  questions: DiscussionFormalQuestion[];
  displayManageeFormSection: boolean;
  displayManagerFormSection: boolean;
  readonly: boolean;
}

@Injectable()
export class DiscussionFormService {
  private fb = inject(NonNullableFormBuilder);
  private destroyRef = inject(DestroyRef);

  readonly form: DiscussionFormGroup = this.fb.group({
    coreValues: this.fb.control(new Ratings<ValueRating>([], [])),
    roleRatings: this.fb.control(new Ratings<DiscussionRoleRating | DiscussionSeatRating>([], [])),
    leadership: this.fb.control(new Ratings<DiscussionCommitmentRating>([], [])),
    coaching: this.fb.control(new Ratings<DiscussionCommitmentRating>([], [])),
    customQuestions: this.fb.control([] as DiscussionCustomQuestionFormValue),
  });

  init(config: FormConfig) {
    this.form.clearValidators();
    this.form.addValidators(
      forbiddenDiscussionFormValidator(config.mode, config.displayManageeFormSection, config.displayManagerFormSection)
    );
    this.form.patchValue(Ratings.forDiscussion(config.discussion));
    this.form.updateValueAndValidity();
    this.requireSaveAfterChange();

    if (config.readonly) {
      this.form.disable({ emitEvent: false });
    }
  }

  saved = true;

  requireSaveAfterChange() {
    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.setSaved(false))
      )
      .subscribe();
  }

  setSaved(saved: boolean) {
    this.saved = saved;
  }
}
